import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import SyncIcon from '@mui/icons-material/SyncOutlined';
import { useAuth } from '../../context/authContext';
import { createFeeChangeRequest, getStudentFeeBreakdown, syncFeeVersionFromMasterData } from '../../api/studentFees';
import { FeeBreakdownItem } from '../../types/studentFees';

const formatAmount = (amount: number | null, currency: { code: string; symbol: string | null } | null) =>
  amount == null ? '—' : `${currency?.symbol ?? currency?.code ?? ''} ${amount.toFixed(2)}`;

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

/**
 * Student-Wise Payment Customization (Phase 52) — the university default
 * (kept exactly as-is by PaymentChecklistCard above this) shown alongside
 * the student's own current amount, with the difference highlighted, a
 * clear Default/Pending/Approved-override status, and a dialog to submit
 * a multi-item fee change request for approval. Never edits an amount
 * directly — every change goes through the existing approval workflow.
 */
export const StudentFeeBreakdownCard = ({ studentId }: { studentId: number }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canRequest = hasPermission('fee_changes', 'request');
  const canSync = hasPermission('fee_changes', 'approve');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['student-fee-breakdown', studentId],
    queryFn: () => getStudentFeeBreakdown(studentId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['student-fee-breakdown', studentId] });

  const submitMutation = useMutation({
    mutationFn: () => {
      const currentByCategory = new Map((query.data?.items ?? []).map((item) => [item.categoryId, item.studentAmount]));
      // Only genuinely changed amounts become part of the request (§7) —
      // the dialog pre-fills every field with its current value so the
      // user sees the full breakdown, but re-submitting an unchanged
      // value must never appear in the audit trail as if it were edited.
      const changedItems = Object.entries(edits)
        .filter(([categoryId, value]) => value.trim() !== '' && Number(value) !== currentByCategory.get(Number(categoryId)))
        .map(([categoryId, value]) => ({ paymentCategoryId: Number(categoryId), newAmount: Number(value) }));
      return createFeeChangeRequest(studentId, changedItems, reason);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEdits({});
      setReason('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const syncMutation = useMutation({
    mutationFn: () => syncFeeVersionFromMasterData(studentId),
    onSuccess: () => {
      invalidate();
      setSyncConfirmOpen(false);
      setSyncError(null);
    },
    onError: (err) => setSyncError(extractErrorMessage(err)),
  });

  if (!query.data) return null;
  const { items, totalDefault, totalStudent, feeSource, pendingRequest, canSyncFromMasterData, feeStructureLabel } =
    query.data;

  const openDialog = () => {
    const initial: Record<number, string> = {};
    items.forEach((item) => {
      if (item.studentAmount != null) initial[item.categoryId] = String(item.studentAmount);
    });
    setEdits(initial);
    setReason('');
    setError(null);
    setDialogOpen(true);
  };

  const hasEdits = (item: FeeBreakdownItem) => {
    const value = edits[item.categoryId];
    return value !== undefined && value.trim() !== '' && Number(value) !== item.studentAmount;
  };
  const anyEdited = items.some(hasEdits);

  const statusChip = pendingRequest ? (
    <Chip size="small" color="warning" label={`Fee Change Pending Approval (${pendingRequest.requestNumber})`} />
  ) : feeSource === 'STUDENT_SPECIFIC' ? (
    <Chip size="small" color="success" label="Student-Specific Fees Approved" />
  ) : (
    <Chip size="small" color="success" variant="outlined" label="Using Default" />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle1">Fee Breakdown</Typography>
          {statusChip}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canSync && canSyncFromMasterData && (
            <Button size="small" startIcon={<SyncIcon />} onClick={() => setSyncConfirmOpen(true)}>
              Sync from Master Data
            </Button>
          )}
          {canRequest && !pendingRequest && (
            <Button size="small" startIcon={<EditIcon />} onClick={openDialog}>
              Request fee change
            </Button>
          )}
        </Stack>
      </Stack>
      {feeStructureLabel && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          Fee Source: {feeStructureLabel}
        </Typography>
      )}

      <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fee</TableCell>
            <TableCell align="right">University Default</TableCell>
            <TableCell align="right">Student Amount</TableCell>
            <TableCell align="right">Difference</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const diff = item.isOverridden && item.defaultAmount != null && item.studentAmount != null
              ? item.studentAmount - item.defaultAmount
              : 0;
            return (
              <TableRow key={item.categoryId}>
                <TableCell>{item.name}</TableCell>
                <TableCell align="right">{formatAmount(item.defaultAmount, item.currency)}</TableCell>
                <TableCell align="right">
                  <Typography component="span" sx={{ fontWeight: item.isOverridden ? 700 : 400 }}>
                    {formatAmount(item.studentAmount, item.currency)}
                  </Typography>
                  {item.isOverridden && <Typography component="span" sx={{ ml: 0.5 }}>⚠</Typography>}
                </TableCell>
                <TableCell align="right">
                  {item.isOverridden ? (
                    <Typography variant="body2" color={diff < 0 ? 'success.main' : 'error.main'}>
                      {diff > 0 ? '+' : ''}
                      {diff.toFixed(2)}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={3}>
          <Typography variant="body2">
            <strong>Default Total:</strong> {totalDefault.toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Student Total:</strong> {totalStudent.toFixed(2)}
          </Typography>
        </Stack>
      </Box>

      {pendingRequest && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>{pendingRequest.requestNumber}</strong> — requested by {pendingRequest.requestedBy.firstName}{' '}
          {pendingRequest.requestedBy.lastName} on {new Date(pendingRequest.requestedAt).toLocaleDateString()}.
          Awaiting approval; the current amounts above remain active until then.
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a student-specific fee change</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {items.map((item) => (
              <TextField
                key={item.categoryId}
                label={`${item.name} (default: ${formatAmount(item.defaultAmount, item.currency)})`}
                type="number"
                fullWidth
                value={edits[item.categoryId] ?? ''}
                onChange={(e) => setEdits((prev) => ({ ...prev, [item.categoryId]: e.target.value }))}
              />
            ))}
            <TextField
              label="Fee Change Reason"
              required
              multiline
              minRows={2}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!anyEdited || !reason.trim() || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            {submitMutation.isPending ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={syncConfirmOpen} onClose={() => setSyncConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sync default fees from Master Data?</DialogTitle>
        <DialogContent>
          {syncError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
              {syncError}
            </Alert>
          )}
          <Typography variant="body2">
            This will update fees that are currently using the default structure to match the latest published Master
            Data. Approved student-specific overrides will not be changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={syncMutation.isPending} onClick={() => syncMutation.mutate()}>
            {syncMutation.isPending ? 'Syncing…' : 'Sync Latest Default'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
